import { useState, useCallback, useEffect, useMemo } from "react";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { MultiFileDropzone } from "@scripts/react/components/BatchProcessing/MultiFileDropzone";
import Button from "@scripts/react/components/Button";
import { Chip } from "@scripts/react/components/Chip";
import { useDeployContext } from "@/contexts/DeployContext";
import InlineAlert from "@scripts/react/components/InlineAlert";
import {
    useCurrentWorkspace,
    useBatchJobs,
    useBatchJobUploadState,
    useBatchJobUploadError,
    useBatchJobFailedFiles,
    useUploadAndTriggerBatchJob,
    useResetBatchJobUploadState
} from "@/store";
import {
    getJobStatus,
    getJobFileCount,
    getJobExportBatchId,
    fetchBatchParts,
    fetchPartFiles,
    type BatchJob,
    type PartMetadata
} from "@/utils/api/batchProcessing";
import { triggerDownload } from "@/utils/browser";
import { DropdownMenu } from "@/components/DropdownMenu";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";

interface BatchProcessingModalProps {
    isOpen: boolean;
    onClose: () => void;
    jobType: "images" | "videos";
    showJobs?: boolean;
}

const UPPERCASE_WORDS = new Set(["csv", "json", "api"]);

function toReadablePartName(partName: string): string {
    return partName
        .replace(/_/g, " ")
        .replace(/\b\w+/g, (word) =>
            UPPERCASE_WORDS.has(word.toLowerCase())
                ? word.toUpperCase()
                : word.charAt(0).toUpperCase() + word.slice(1)
        );
}

type JobStatus = "processing" | "downloading" | "done" | "error";

function JobStatusBadge({ status }: { status: JobStatus }) {
    if (status === "done") {
        return (
            <Chip color="green" size="sm">
                <i className="far fa-check" /> Done
            </Chip>
        );
    }
    if (status === "downloading") {
        return (
            <Chip color="yellow" size="sm">
                Downloading
            </Chip>
        );
    }
    if (status === "error") {
        return (
            <Chip color="red" size="sm">
                <i className="far fa-times" /> Error
            </Chip>
        );
    }
    return (
        <Chip color="brand" size="sm">
            <i className="far fa-spinner fa-spin" /> Processing...
        </Chip>
    );
}

function JobItem({ job, workspaceId }: { job: BatchJob; workspaceId: string }) {
    const { track } = useAnalytics();
    const [showDetails, setShowDetails] = useState(false);
    const [parts, setParts] = useState<PartMetadata[] | null>(null);
    const [loadingPartName, setLoadingPartName] = useState<string | null>(null);

    const status = getJobStatus(job);
    const fileCount = getJobFileCount(job);
    const exportBatchId = getJobExportBatchId(job);

    const toggleDetails = useCallback(() => setShowDetails((v) => !v), []);

    const createdDate = job.creationTimestamp?.toDate
        ? job.creationTimestamp.toDate()
        : job.creationTimestamp
          ? new Date(job.creationTimestamp)
          : null;

    // Pre-fetch parts when the job has results
    useEffect(() => {
        if (!exportBatchId || parts) return;
        fetchBatchParts(workspaceId, exportBatchId).then(setParts);
    }, [exportBatchId, parts, workspaceId]);

    const DOWNLOAD_ALL_PART_NAME = "download_all";

    const handlePartSelect = useCallback(
        async (part: PartMetadata) => {
            if (loadingPartName || !parts) return;
            track({
                event: AnalyticsFlowEvent.BATCH_DOWNLOAD_CLICKED,
                data: { partName: part.partName }
            });
            if (part.partName === DOWNLOAD_ALL_PART_NAME) {
                setLoadingPartName(DOWNLOAD_ALL_PART_NAME);
                for (const p of parts) {
                    const { filesMetadata } = await fetchPartFiles(
                        workspaceId,
                        p.batchId,
                        p.partName
                    );
                    for (const file of filesMetadata) {
                        triggerDownload(file.downloadURL, file.fileName);
                    }
                }
                setLoadingPartName(null);
                return;
            }
            setLoadingPartName(part.partName);
            const { filesMetadata } = await fetchPartFiles(
                workspaceId,
                part.batchId,
                part.partName
            );
            for (const file of filesMetadata) {
                triggerDownload(file.downloadURL, file.fileName);
            }
            setLoadingPartName(null);
        },
        [loadingPartName, workspaceId, parts, track]
    );

    const dropdownItems = useMemo(() => {
        if (!parts || !exportBatchId) return [];
        return [
            ...parts,
            {
                batchId: exportBatchId,
                partName: DOWNLOAD_ALL_PART_NAME,
                partDescription: "JSON, CSV, and visualizations",
                contentType: "application/zip"
            }
        ];
    }, [parts, exportBatchId]);

    const getItemLabel = useCallback(() => "", []);
    const getItemKey = useCallback((part: PartMetadata) => part.partName, []);
    const getLeftContent = useCallback(
        (part: PartMetadata) => (
            <div className="flex flex-col text-left text-sm">
                <span>{toReadablePartName(part.partName)}</span>
                {part.partDescription && (
                    <span className="text-sm font-normal text-tertiary">
                        {part.partDescription}
                    </span>
                )}
            </div>
        ),
        []
    );
    const getRightContent = useCallback(
        () => <i className="far fa-arrow-down-to-line text-xs text-gray-400" />,
        []
    );

    return (
        <div className="border-b border-gray-200 last:border-b-0">
            <div
                className="flex h-12 cursor-pointer items-center justify-between py-3 pl-4 pr-3"
                onClick={toggleDetails}
            >
                <div className="flex items-center gap-2">
                    <i className="far fa-images text-base text-secondary" />
                    <span className="text-sm font-medium text-secondary">
                        {fileCount ? `${fileCount} files` : "Files"}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <JobStatusBadge status={status} />
                    {exportBatchId && (
                        <DropdownMenu
                            items={dropdownItems}
                            selectedItem={dropdownItems[0]}
                            onSelectItem={handlePartSelect}
                            getItemLabel={getItemLabel}
                            getItemKey={getItemKey}
                            getLeftContent={getLeftContent}
                            getRightContent={getRightContent}
                            customMenuButton={
                                <i
                                    className={`far ${parts === null ? "fa-spinner fa-spin" : "fa-arrow-down-to-line"}`}
                                />
                            }
                            buttonClassName="btn2 secondary !py-0 !px-3 !min-h-0 text-xs"
                            menuItemsClassName="right-0 mt-1 text-base"
                            disabled={parts === null || dropdownItems.length === 0}
                        />
                    )}
                </div>
            </div>
            {showDetails && createdDate && (
                <div className="px-4 pb-3 text-xs text-gray-500">
                    Created On:{" "}
                    {createdDate.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit"
                    })}
                    ,{" "}
                    {createdDate.toLocaleTimeString(undefined, {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true
                    })}
                </div>
            )}
        </div>
    );
}

export function BatchProcessingModal({
    isOpen,
    onClose,
    jobType,
    showJobs = true
}: BatchProcessingModalProps) {
    const { workflowCredentials } = useDeployContext();
    const workspace = useCurrentWorkspace();
    const jobs = useBatchJobs();
    const uploadState = useBatchJobUploadState();
    const uploadError = useBatchJobUploadError();
    const failedFiles = useBatchJobFailedFiles();
    const uploadAndTrigger = useUploadAndTriggerBatchJob();
    const resetUploadState = useResetBatchJobUploadState();

    const { track } = useAnalytics();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showJobsSidebar, setShowJobsSidebar] = useState(showJobs);

    useEffect(() => {
        if (isOpen) {
            setShowJobsSidebar(showJobs);
        }
    }, [isOpen, showJobs]);

    const workspaceId = workspace?.id;
    const isUploading = uploadState === "uploading" || uploadState === "triggering";
    const hasJobs = showJobsSidebar && jobs.length > 0;

    const handleFilesSelected = useCallback(
        (files: File[]) => {
            setSelectedFiles(files);
            if (files.length > 0) {
                track({
                    event: AnalyticsFlowEvent.BATCH_FILES_SELECTED,
                    data: { fileCount: files.length, jobType }
                });
            }
        },
        [track, jobType]
    );

    const handleRunModel = useCallback(async () => {
        if (!workspaceId || selectedFiles.length === 0 || isUploading) return;

        track({
            event: AnalyticsFlowEvent.BATCH_RUN_MODEL_CLICKED,
            data: { fileCount: selectedFiles.length, jobType }
        });

        await uploadAndTrigger({ workspaceId, files: selectedFiles, jobType, workflowCredentials });

        track({ event: AnalyticsFlowEvent.BATCH_JOB_SUBMITTED, data: { jobType } });

        setSelectedFiles([]);
        setShowJobsSidebar(true);
    }, [
        workspaceId,
        selectedFiles,
        jobType,
        workflowCredentials,
        isUploading,
        uploadAndTrigger,
        track
    ]);

    const handleClose = useCallback(() => {
        resetUploadState();
        setSelectedFiles([]);
        onClose();
    }, [onClose, resetUploadState]);

    const buttonLabel = useMemo(() => {
        if (isUploading) {
            return uploadState === "triggering" ? "Starting job..." : "Uploading...";
        }
        if (selectedFiles.length > 0) {
            return `Run Model on ${selectedFiles.length} ${selectedFiles.length === 1 ? "file" : "files"}`;
        }
        return "Run Model";
    }, [isUploading, uploadState, selectedFiles.length]);

    return (
        <ModalWithTitle
            isOpen={isOpen}
            onClose={handleClose}
            title="Process Files in Bulk"
            size="xxl"
            showFooter={false}
            scrollInsideModal={true}
            panelClassName="h-screen"
            className="!p-0"
        >
            <div className="flex flex-1 flex-col gap-6 p-6">
                {/* Description */}
                <div>
                    <p className="text-base text-primary">
                        Process large volumes of videos and images without writing code. Your{" "}
                        {jobType === "images" ? "images" : "videos"} will be processed in jobs that
                        run in the background.
                    </p>
                    {hasJobs && (
                        <p className="text-base text-primary">
                            You can exit and return to this modal at any time to see your batch job
                            progress. We will also email you when it&apos;s done processing.
                        </p>
                    )}
                </div>

                {/* Main content area */}
                <div className={`flex min-h-0 flex-1 ${hasJobs ? "gap-8" : "flex-col gap-3"}`}>
                    {/* Left: upload zone */}
                    <div
                        className={`flex min-h-0 flex-1 flex-col gap-3 ${hasJobs ? "min-w-0" : "w-full"}`}
                    >
                        <h3 className="text-md font-semibold text-primary">Upload Your Files</h3>
                        <MultiFileDropzone
                            onFilesSelected={handleFilesSelected}
                            selectedFiles={selectedFiles}
                            jobType={jobType}
                            acceptedFileTypes={jobType === "images" ? "image/*" : "video/*"}
                            className="flex-1"
                        />
                        {uploadState === "error" && uploadError && (
                            <InlineAlert level="danger" type="primary" dense>
                                <p>
                                    <strong>{uploadError}.</strong>
                                    {failedFiles.length > 0 && (
                                        <>
                                            {" "}
                                            {failedFiles.map((f) => f.fileName).join(", ")} (
                                            {failedFiles[0].reason})
                                        </>
                                    )}
                                </p>
                            </InlineAlert>
                        )}
                        {uploadState === "done" && failedFiles.length > 0 && (
                            <InlineAlert level="warning" type="primary" dense>
                                <p>
                                    <strong>
                                        {failedFiles.length}{" "}
                                        {failedFiles.length === 1 ? "file" : "files"} failed to
                                        upload:
                                    </strong>{" "}
                                    {failedFiles
                                        .map((f) => `${f.fileName} (${f.reason})`)
                                        .join(", ")}
                                </p>
                            </InlineAlert>
                        )}
                        <Button
                            type="primary"
                            disabled={isUploading || selectedFiles.length === 0}
                            onClick={handleRunModel}
                            className="w-full"
                        >
                            {buttonLabel}
                        </Button>
                    </div>

                    {/* Right: Jobs sidebar */}
                    {hasJobs && (
                        <div className="flex w-72 shrink-0 flex-col rounded-lg border border-gray-300">
                            <div className="flex h-12 shrink-0 items-center rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
                                <span className="text-sm font-medium text-primary">Jobs</span>
                            </div>
                            <div className="flex-1">
                                {jobs.map((job) => (
                                    <JobItem
                                        key={job.id}
                                        job={job}
                                        workspaceId={workspaceId ?? ""}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ModalWithTitle>
    );
}
